import { createReducer } from 'store/createReducer';
import {
  ConfiguratorChangeValue,
  CONFIGURATOR_CHANGE_VALUE,
  CONFIGURATOR_DELETE_VALUE,
  CONFIGURATOR_FIELD_BLUR,
  CONFIGURATOR_RESET
} from 'views/ProductPage/configurators/actions';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { PRODUCT_PAGE_ADDITIONAL_DATA_SET } from 'views/ProductPage/actions';

import {
  SELECT_CONFIGURATOR_ADDON,
  SelectInstallationProductAction,
  SELECT_CONFIGURATOR_INSTALLATION,
  RESET_INSTALLATION_ADDONS_ACTION,
  RESET_INSTALLATION_CONFIGURATOR,
  RESET_INSTALLATION_ACTION
} from './installation/actions';
import { PRODUCT_PAGE_VARIANT_DATA_SET, ProductPageVariantDataAction } from '../actions';
import { isInstallationConfigurator, Nullable } from './selector';
import {
  InstallationConfigurator,
  IInstallationAddonProduct
} from 'models/InstallationConfigurator';

const mapProducts = (
  state: InstallationConfigurator,
  keys: ('installation' | 'addons')[],
  mapFn: (product: IInstallationAddonProduct) => IInstallationAddonProduct
): InstallationConfigurator => {
  const updatedProductSections = keys.reduce((obj, key) => {
    const currentSection = state[key];
    return {
      ...obj,
      [key]: {
        ...currentSection,
        products: currentSection ? currentSection.products.map(mapFn) : []
      }
    };
  }, {});
  return {
    ...state,
    ...updatedProductSections
  };
};

const selectProduct = (productId: string) => (product: IInstallationAddonProduct) => ({
  ...product,
  selected: product.productId === productId
});

const unselectProduct = (product: IInstallationAddonProduct) => ({
  ...product,
  selected: false
});

export const configurator = createReducer<Nullable<ConfiguratorBase & { pristine: boolean }>>(
  {
    type: null,
    attributes: null,
    variants: null,
    values: null,
    pristine: true
  },
  {
    [CONFIGURATOR_FIELD_BLUR]: state => ({
      ...state,
      pristine: true
    }),
    [PRODUCT_PAGE_ADDITIONAL_DATA_SET]: state => ({
      ...state,
      pristine: true
    }),
    [CONFIGURATOR_CHANGE_VALUE]: (state, { key, value }: ConfiguratorChangeValue) => ({
      ...state,
      pristine: false,
      values: {
        ...state.values,
        [key]: value
      }
    }),
    [CONFIGURATOR_DELETE_VALUE]: (state, { key }) => {
      const values = { ...state.values, [key]: undefined };
      return {
        ...state,
        values
      };
    },
    [CONFIGURATOR_RESET]: state => {
      const previousValues = state.values;
      if (!previousValues) {
        return {
          ...state,
          pristine: true,
          values: {}
        };
      }

      const valuesToSave = ['zipCodeList', 'zipCode'];
      const values = Object.fromEntries(
        Object.entries(previousValues).filter(([key]) => valuesToSave.includes(key))
      );

      return {
        ...state,
        pristine: true,
        values
      };
    },
    [PRODUCT_PAGE_VARIANT_DATA_SET]: (state, { type, ...action }: ProductPageVariantDataAction) => {
      const hasConfigurator = 'configurator' in action.data;
      if (!hasConfigurator) return state;

      return {
        ...action.data.configurator,
        values: state.values
      };
    },
    [SELECT_CONFIGURATOR_INSTALLATION]: (state, action: SelectInstallationProductAction) => {
      const _state = isInstallationConfigurator(state)
        ? mapProducts(state, ['installation'], selectProduct(action.productId))
        : state;

      return _state;
    },
    [SELECT_CONFIGURATOR_ADDON]: (state, action: SelectInstallationProductAction) => {
      return isInstallationConfigurator(state)
        ? mapProducts(state, ['addons'], selectProduct(action.productId))
        : state;
    },
    [RESET_INSTALLATION_ADDONS_ACTION]: state => {
      return isInstallationConfigurator(state)
        ? mapProducts(state, ['addons'], unselectProduct)
        : state;
    },
    [RESET_INSTALLATION_ACTION]: state => {
      return isInstallationConfigurator(state)
        ? mapProducts(state, ['installation'], unselectProduct)
        : state;
    },
    [RESET_INSTALLATION_CONFIGURATOR]: state => {
      return isInstallationConfigurator(state)
        ? mapProducts(state, ['addons', 'installation'], unselectProduct)
        : state;
    }
  }
);
